import { useEffect, useState } from "react";
import { Star, BadgeCheck, ArrowUpRight, Quote } from "lucide-react";
import api from "../lib/api";
import { useSettings } from "../lib/useSettings";
import { FadeUp } from "./Motion";
import { parseVideoUrl } from "../lib/video";

// Google-style reviews block: header (rating + verified badge + leave review CTA)
// + curated testimonials grid (pulled from CMS Testimonials).
export default function GoogleReviews({ compact = false }) {
  const settings = useSettings();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  const rating = settings?.rating_value || "4.9";
  const count = settings?.rating_count || 120;
  const businessName = settings?.business_name || "NorthCrest Landscaping";
  const reviewUrl = settings?.google_reviews_url ||
    `https://search.google.com/local/writereview?placeid=&q=${encodeURIComponent(businessName + " Edmonton")}`;
  const seeAllUrl = settings?.google_reviews_url ||
    `https://www.google.com/search?q=${encodeURIComponent(businessName + " Edmonton reviews")}`;

  // Show 3 best (5★) reviews by default, or all of them if fewer
  const displayed = (compact ? reviews.slice(0, 3) : reviews)
    .filter((r) => (r.rating || 5) >= 4)
    .slice(0, 6);

  return (
    <section data-testid="google-reviews-block" className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-y border-[#D8D6D0]/10">
      {/* Header */}
      <FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-16 items-end">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 overline text-[#8C1D30] mb-4">
              <BadgeCheck className="w-4 h-4" /> Verified by Google
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight">
              Rated <span className="italic text-[#D8D6D0]">{rating}★</span> by Edmonton homeowners.
            </h2>
            <p className="text-[#D8D6D0]/60 mt-6 max-w-lg text-base md:text-lg">
              {count}+ Google reviews. Real customers. Real builds. Read every word — we don&apos;t filter.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:items-end">
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300"
            >
              <Star className="w-4 h-4" /> Leave a Google Review
            </a>
            <a
              href={seeAllUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="see-all-reviews-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF]/5 text-[#F5F3EF] text-xs tracking-wider uppercase transition-colors duration-300"
            >
              See all reviews <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </FadeUp>

      {/* Stats bar — Google-style */}
      <FadeUp delay={0.1}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 bg-[#0d0d0d] border border-[#D8D6D0]/10 p-6 md:p-8">
          <div>
            <div className="font-serif-display text-5xl md:text-6xl text-[#F5F3EF]">{rating}</div>
            <div className="flex gap-0.5 mt-2">
              {[0,1,2,3,4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#E2B85A] stroke-[#E2B85A]" />
              ))}
            </div>
            <div className="overline text-[10px] text-[#D8D6D0]/50 mt-2">Average Rating</div>
          </div>
          <div>
            <div className="font-serif-display text-5xl md:text-6xl text-[#F5F3EF]">{count}+</div>
            <div className="overline text-[10px] text-[#D8D6D0]/50 mt-4">Google Reviews</div>
          </div>
          <div>
            <div className="font-serif-display text-5xl md:text-6xl text-[#F5F3EF]">98%</div>
            <div className="overline text-[10px] text-[#D8D6D0]/50 mt-4">5-Star Reviews</div>
          </div>
          <div>
            <div className="flex items-center gap-2 mt-1">
              <BadgeCheck className="w-7 h-7 text-[#4285F4]" />
              <div className="font-serif-display text-2xl md:text-3xl text-[#F5F3EF] leading-tight">Verified<br/>Business</div>
            </div>
            <div className="overline text-[10px] text-[#D8D6D0]/50 mt-2">Google Business Profile</div>
          </div>
        </div>
      </FadeUp>

      {/* Reviews grid */}
      {displayed.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((r, i) => {
            const video = parseVideoUrl(r.video_url);
            return (
            <FadeUp key={r.id} delay={i * 0.05}>
              <article data-testid={`review-card-${r.id}`} className="group h-full bg-[#0d0d0d] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors p-7 flex flex-col">
                {video && (
                  <div data-testid={`review-video-${r.id}`} className="mb-5 -mx-1 aspect-video bg-[#151515] overflow-hidden">
                    {video.kind === "iframe" ? (
                      <iframe
                        title={`${r.author} testimonial`}
                        src={video.embed}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        className="w-full h-full"
                      />
                    ) : (
                      <video src={video.src} controls preload="metadata" className="w-full h-full object-cover" />
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[0,1,2,3,4].map((j) => (
                      <Star key={j} className={`w-3.5 h-3.5 ${j < (r.rating || 5) ? "fill-[#E2B85A] stroke-[#E2B85A]" : "stroke-[#D8D6D0]/30"}`} />
                    ))}
                  </div>
                  <Quote className="w-5 h-5 text-[#8C1D30]/40" />
                </div>
                <p className="text-[#D8D6D0]/85 leading-relaxed flex-1 line-clamp-6">&ldquo;{r.quote}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-[#D8D6D0]/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#151515] overflow-hidden flex-shrink-0 border border-[#D8D6D0]/15">
                    {r.avatar ? (
                      <img
                        src={r.avatar.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${r.avatar}` : r.avatar}
                        alt={r.author}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/50 font-serif-display">{r.author.charAt(0)}</div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif-display text-base truncate">{r.author}</div>
                    <div className="overline text-[10px] text-[#D8D6D0]/50 mt-0.5 truncate">
                      {r.location}{r.project_type ? ` · ${r.project_type}` : ""}
                    </div>
                  </div>
                </div>
              </article>
            </FadeUp>
            );
          })}
        </div>
      )}
    </section>
  );
}
